// FILE: apps/admin/src/pages/Payments.tsx
// VERSION: 1.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Incoming T-Bank SBP/card payments monitor with responsive mobile-cards layout, matched/unmatched filters, and manual order binding dialog.
//   SCOPE: Payments component.
//   DEPENDS: ../api.js, ../display-currency.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Payments - Admin Incoming Payments page
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, dt, rub } from '../api.js';
import { useDisplayCurrency } from '../display-currency.js';
import { useToast } from '../toast.js';

interface Payment {
  id: string;
  amountKopecks: number;
  sender: string;
  bankOperationId: string;
  observedAt: string;
  matchedOrderId: string | null;
}

// START_CONTRACT: Payments
//   PURPOSE: Render the Incoming Payments page with filter tabs and manual order matching modal.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/payments and POST /admin/payments/:id/match
//   LINKS: M-ADMIN
// END_CONTRACT: Payments
export function Payments() {
  // START_BLOCK_PAYMENTS
  const [matched, setMatched] = useState<'all' | 'yes' | 'no'>('all');
  const [bindPayment, setBindPayment] = useState<Payment | null>(null);
  const [orderIdInput, setOrderIdInput] = useState('');
  const { money } = useDisplayCurrency();
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['payments', matched],
    queryFn: () => api.get<Payment[]>(`/admin/payments?matched=${matched}&limit=200`),
    refetchInterval: 12000,
  });

  const match = useMutation({
    mutationFn: ({ id, orderId }: { id: string; orderId: string }) =>
      api.post<{ ok: boolean; reason?: string }>(`/admin/payments/${id}/match`, { orderId }),
    onSuccess: (r) => {
      toast(r.ok ? 'Платёж привязан — заказ передан в выдачу' : (r.reason ?? 'Не удалось привязать'));
      if (r.ok) {
        setBindPayment(null);
        setOrderIdInput('');
        void qc.invalidateQueries({ queryKey: ['payments'] });
        void qc.invalidateQueries({ queryKey: ['orders'] });
      }
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка привязки'),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Входящие платежи Т-Банк</h1>
          <div className="page-subtitle">
            Поток банковских операций СБП/карта и ручная привязка несопоставленных переводов
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className={`tab-btn ${matched === 'all' ? 'active' : ''}`}
            onClick={() => setMatched('all')}
          >
            Все операции
          </button>
          <button
            type="button"
            className={`tab-btn ${matched === 'no' ? 'active' : ''}`}
            onClick={() => setMatched('no')}
          >
            Не сопоставленные
          </button>
          <button
            type="button"
            className={`tab-btn ${matched === 'yes' ? 'active' : ''}`}
            onClick={() => setMatched('yes')}
          >
            Сопоставленные
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="muted">Загрузка входящих платежей…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Сумма</th>
                  <th>Отправитель</th>
                  <th>Bank Op ID</th>
                  <th>Обнаружен</th>
                  <th>Заказ</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className="mono" data-label="Сумма" style={{ fontWeight: 700 }}>
                      {money(p.amountKopecks)}
                      <div className="muted" style={{ fontSize: 11 }}>{rub(p.amountKopecks)} ₽</div>
                    </td>
                    <td data-label="Отправитель">{p.sender || '—'}</td>
                    <td className="mono muted" data-label="Bank Op ID">{p.bankOperationId}</td>
                    <td className="muted" data-label="Обнаружен">{dt(p.observedAt)}</td>
                    <td className="mono" data-label="Заказ">
                      {p.matchedOrderId ? (
                        <span className="chip DELIVERED">
                          {p.matchedOrderId.slice(0, 8).toUpperCase()}
                        </span>
                      ) : (
                        <span className="chip AWAITING_PAYMENT">не сопоставлен</span>
                      )}
                    </td>
                    <td className="mobile-card-actions" data-label="">
                      {!p.matchedOrderId && (
                        <button
                          type="button"
                          className="btn sm primary"
                          onClick={() => {
                            setBindPayment(p);
                            setOrderIdInput('');
                          }}
                        >
                          Привязать к заказу
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!data?.length && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Платежей по выбранному фильтру нет
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {bindPayment && (
        <div className="modal-backdrop" onClick={() => setBindPayment(null)}>
          <div
            className="modal-card"
            style={{ maxWidth: 'min(480px, 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Привязка платежа {rub(bindPayment.amountKopecks)} ₽</h2>
            <p className="muted" style={{ fontSize: 13, overflowWrap: 'anywhere' }}>
              Отправитель: <strong>{bindPayment.sender}</strong> · Операция:{' '}
              <span className="mono">{bindPayment.bankOperationId}</span>
            </p>
            <div style={{ marginTop: 14 }}>
              <label className="muted" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                Полный UUID заказа для зачисления оплаты:
              </label>
              <input
                style={{ width: '100%', minWidth: 0 }}
                placeholder="например: 550e8400-e29b-41d4-a716-446655440000"
                value={orderIdInput}
                onChange={(e) => setOrderIdInput(e.target.value)}
              />
            </div>
            <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn ghost" onClick={() => setBindPayment(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={!orderIdInput.trim() || match.isPending}
                onClick={() =>
                  match.mutate({ id: bindPayment.id, orderId: orderIdInput.trim() })
                }
              >
                {match.isPending ? 'Привязка…' : 'Сматчить и доставить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  // END_BLOCK_PAYMENTS
}
